import React from "react";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import { Card, CardBody, CardHeader } from "../../ui/Card";

const STORAGE_KEY = "estimation_station_inspection_checklist_v1";

const SURFACE_FLAGS = ["water stain", "detach/reset", "replace", "seal/prime", "paint", "texture match"];
const CEILING_TEXTURES = ["smooth", "orange peel", "knockdown", "popcorn", "skip trowel"];
const WALL_TEXTURES = ["smooth", "orange peel", "knockdown", "skip trowel", "wallpaper"];
const CEILING_MATERIALS = ["drywall", "plaster", "acoustical tile", "wood plank", "tongue and groove"];
const WALL_MATERIALS = ["drywall", "plaster", "paneling", "tile", "FRP", "wallpaper", "baseboard", "crown molding", "chair rail", "door casing", "window casing", "wainscot"];
const FLOOR_MATERIALS = ["LVP", "laminate", "engineered wood", "hardwood", "tile", "carpet", "sheet vinyl", "concrete"];
const INSULATION_TYPES = ["none visible", "fiberglass batt", "blown-in", "foam board", "spray foam"];
const SUBFLOOR_TYPES = ["OSB", "plywood", "plank", "concrete", "unknown"];
const UNDERLAYMENT_TYPES = ["none visible", "pad", "cork", "foam", "mortar bed"];
const WINDOW_TYPES = ["single pane", "double pane", "tempered", "slider", "picture", "casement"];
const WINDOW_ACTIONS = ["replace sash", "replace unit", "detach/reset trim", "board up", "glass only"];
const DOOR_MATERIALS = ["hollow core", "solid core", "wood", "fiberglass", "metal", "French door", "slider"];
const DOOR_ACTIONS = ["replace", "detach/reset", "refinish", "hardware reset"];
const CABINET_MATERIALS = ["oak", "maple", "paint-grade", "thermofoil", "laminate", "custom stain grade"];
const CABINET_ACTIONS = ["detach/reset", "total replacement", "refacing", "site repair"];
const EXTERIOR_MATERIALS = ["composition shingle", "metal", "tile", "stucco", "lap siding", "brick veneer", "stone veneer"];

function uid(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function createSurface(kind) {
  if (kind === "floor") {
    return {
      material: "",
      materials: [],
      width: "",
      subfloor: "",
      underlayment: "",
      lineItem: "",
      notes: "",
      flags: [],
    };
  }
  return {
    material: "",
    materials: [],
    width: "",
    texture: "",
    insulation: "",
    lineItem: "",
    notes: "",
    flags: [],
  };
}

function createInteriorRoom() {
  return {
    id: uid("room"),
    roomName: "",
    area: "",
    notes: "",
    ceiling: createSurface("ceiling"),
    walls: createSurface("walls"),
    floor: createSurface("floor"),
  };
}

function createWindowEntry() {
  return {
    id: uid("window"),
    roomName: "",
    size: "",
    material: "",
    pane: [],
    action: [],
    notes: "",
  };
}

function createDoorEntry() {
  return {
    id: uid("door"),
    roomName: "",
    size: "",
    material: "",
    action: [],
    notes: "",
  };
}

function createCabinetEntry() {
  return {
    id: uid("cabinet"),
    roomName: "",
    material: "",
    custom: "",
    action: [],
    notes: "",
  };
}

function createInitialState() {
  return {
    property: {
      claimName: "",
      insured: "",
      address: "",
      inspector: "",
      inspectionDate: "",
      lossType: "",
      summary: "",
    },
    interiorRooms: [createInteriorRoom()],
    windows: [createWindowEntry()],
    doors: [createDoorEntry()],
    cabinetry: [createCabinetEntry()],
    appliances: {
      refrigerator: { selected: false, options: ["replace", "detach/reset", "clean", "ice maker"], values: [], notes: "" },
      dishwasher: { selected: false, options: ["replace", "detach/reset", "clean"], values: [], notes: "" },
      range: { selected: false, options: ["replace", "detach/reset", "clean", "gas", "electric"], values: [], notes: "" },
      rangeHood: { selected: false, options: ["replace", "detach/reset", "clean"], values: [], notes: "" },
      microwave: { selected: false, options: ["replace", "detach/reset", "clean"], values: [], notes: "" },
      garbageDisposal: { selected: false, options: ["replace", "detach/reset", "clean"], values: [], notes: "" },
    },
    exterior: {
      roof: { material: "", materials: [], size: "", action: [], notes: "" },
      elevations: { material: "", materials: [], size: "", action: [], notes: "" },
    },
  };
}

function readStoredState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialState();
    const parsed = JSON.parse(raw);
    if (!(parsed && typeof parsed === "object")) return createInitialState();
    const next = { ...createInitialState(), ...parsed };
    next.interiorRooms = Array.isArray(next.interiorRooms) ? next.interiorRooms.map((room) => ({
      ...room,
      ceiling: { ...createSurface("ceiling"), ...(room?.ceiling || {}), materials: Array.isArray(room?.ceiling?.materials) ? room.ceiling.materials : [] },
      walls: { ...createSurface("walls"), ...(room?.walls || {}), materials: Array.isArray(room?.walls?.materials) ? room.walls.materials : [] },
      floor: { ...createSurface("floor"), ...(room?.floor || {}), materials: Array.isArray(room?.floor?.materials) ? room.floor.materials : [] },
    })) : createInitialState().interiorRooms;
    next.appliances = Object.fromEntries(
      Object.entries(createInitialState().appliances).map(([key, defaults]) => [
        key,
        { ...defaults, ...(next.appliances?.[key] || {}), values: Array.isArray(next.appliances?.[key]?.values) ? next.appliances[key].values : [] },
      ]),
    );
    next.exterior = Object.fromEntries(
      Object.entries(createInitialState().exterior).map(([key, defaults]) => [
        key,
        {
          ...defaults,
          ...(next.exterior?.[key] || {}),
          materials: Array.isArray(next.exterior?.[key]?.materials) ? next.exterior[key].materials : [],
        },
      ]),
    );
    return next;
  } catch (_e) {
    return createInitialState();
  }
}

function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

function ChipToggle({ label, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "rounded-full border px-3 py-1.5 text-xs font-medium transition",
        selected
          ? "border-red-700 bg-red-700 text-white shadow-sm"
          : "border-stone-300 bg-white text-stone-700 hover:border-stone-400 hover:bg-stone-50",
      )}
    >
      {label}
    </button>
  );
}

function SectionAnchor({ label, href }) {
  return (
    <a
      href={href}
      className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-stone-700 transition hover:border-red-400 hover:text-red-700"
    >
      {label}
    </a>
  );
}

function FieldLabel({ children }) {
  return <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">{children}</label>;
}

function SelectField({ value, onChange, options, placeholder = "Select option" }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="h-10 w-full rounded-xl border border-stone-300 bg-white px-3 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function AddedMaterialList({ items, onRemove }) {
  if (!items.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {items.map((item) => (
        <button
          key={item}
          type="button"
          onClick={() => onRemove(item)}
          className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 transition hover:bg-amber-100"
        >
          <span>{item}</span>
          <span className="text-[10px] uppercase tracking-[0.16em] text-amber-700">remove</span>
        </button>
      ))}
    </div>
  );
}

function SurfaceCard({ title, surface, kind, onChange, onToggleFlag, onAddMaterial, onRemoveMaterial }) {
  const isFloor = kind === "floor";
  const materialOptions = isFloor ? FLOOR_MATERIALS : title === "Ceiling" ? CEILING_MATERIALS : WALL_MATERIALS;
  const textureOptions = title === "Ceiling" ? CEILING_TEXTURES : WALL_TEXTURES;
  const selectedMaterials = Array.isArray(surface.materials) ? surface.materials : [];

  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50/80 p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h4 className="text-base font-semibold text-stone-900">{title}</h4>
          <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Repair-ready capture</p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <FieldLabel>Primary material</FieldLabel>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <SelectField value={surface.material} onChange={(e) => onChange("material", e.target.value)} options={materialOptions} placeholder="Select primary material" />
            </div>
            <Button
              variant="secondary"
              className="shrink-0"
              onClick={() => onAddMaterial(surface.material)}
              disabled={!String(surface.material || "").trim()}
            >
              Add material
            </Button>
          </div>
          <AddedMaterialList items={selectedMaterials} onRemove={onRemoveMaterial} />
        </div>
        <div className="space-y-2">
          <FieldLabel>{isFloor ? "Affected Width / Area" : "Affected Width / Height"}</FieldLabel>
          <Input value={surface.width} onChange={(e) => onChange("width", e.target.value)} placeholder={isFloor ? "e.g. 8 ft x 10 ft" : "e.g. 4 ft x 12 ft"} />
        </div>
        {!isFloor ? (
          <div className="space-y-2">
            <FieldLabel>Texture / Finish</FieldLabel>
            <SelectField value={surface.texture} onChange={(e) => onChange("texture", e.target.value)} options={textureOptions} placeholder="Select finish" />
          </div>
        ) : (
          <div className="space-y-2">
            <FieldLabel>Subfloor</FieldLabel>
            <SelectField value={surface.subfloor} onChange={(e) => onChange("subfloor", e.target.value)} options={SUBFLOOR_TYPES} placeholder="Select subfloor" />
          </div>
        )}
        {!isFloor ? (
          <div className="space-y-2">
            <FieldLabel>Insulation</FieldLabel>
            <SelectField value={surface.insulation} onChange={(e) => onChange("insulation", e.target.value)} options={INSULATION_TYPES} placeholder="Select insulation" />
          </div>
        ) : (
          <div className="space-y-2">
            <FieldLabel>Underlayment</FieldLabel>
            <SelectField value={surface.underlayment} onChange={(e) => onChange("underlayment", e.target.value)} options={UNDERLAYMENT_TYPES} placeholder="Select underlayment" />
          </div>
        )}
      </div>
      <details className="mt-4 rounded-2xl border border-stone-200 bg-white">
        <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-stone-800">
          Scope actions
        </summary>
        <div className="border-t border-stone-200 px-4 py-4">
          <div className="grid gap-2 sm:grid-cols-2">
            {SURFACE_FLAGS.map((flag) => (
              <label key={flag} className="flex items-center gap-2 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={surface.flags.includes(flag)}
                  onChange={() => onToggleFlag(flag)}
                  className="h-4 w-4 rounded border-stone-300 text-red-700 focus:ring-red-300"
                />
                <span>{flag}</span>
              </label>
            ))}
          </div>
        </div>
      </details>
      <details className="mt-4 rounded-2xl border border-stone-200 bg-white">
        <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-stone-800">
          Notes and line item detail
        </summary>
        <div className="grid gap-4 border-t border-stone-200 px-4 py-4 md:grid-cols-[1fr_1.4fr]">
          <div className="space-y-2">
            <FieldLabel>Line item / code note</FieldLabel>
            <Input value={surface.lineItem} onChange={(e) => onChange("lineItem", e.target.value)} placeholder="e.g. flood cut, tape/float, prime/paint" />
          </div>
          <div className="space-y-2">
            <FieldLabel>Field notes</FieldLabel>
            <textarea
              value={surface.notes}
              onChange={(e) => onChange("notes", e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
              placeholder="Add conditions, transitions, moisture findings, or scope clarifiers."
            />
          </div>
        </div>
      </details>
    </div>
  );
}

function countCompletedValues(value) {
  if (!value) return 0;
  if (typeof value === "string") return value.trim() ? 1 : 0;
  if (Array.isArray(value)) return value.length;
  if (typeof value === "object") return Object.values(value).reduce((sum, item) => sum + countCompletedValues(item), 0);
  if (typeof value === "boolean") return value ? 1 : 0;
  return 0;
}

function renderSummary(data) {
  const lines = [];
  if (data.property.claimName || data.property.address) {
    lines.push(`${data.property.claimName || "Inspection"}${data.property.address ? ` - ${data.property.address}` : ""}`);
  }
  for (const room of data.interiorRooms) {
    const roomName = room.roomName || "Unnamed room";
    const roomBits = [];
    ["ceiling", "walls", "floor"].forEach((key) => {
      const surface = room[key];
      const materialTags = [surface.material, ...(Array.isArray(surface.materials) ? surface.materials : [])]
        .filter(Boolean)
        .filter((value, index, arr) => arr.indexOf(value) === index);
      const tags = [...materialTags, surface.width, surface.lineItem, ...(surface.flags || [])].filter(Boolean);
      if (tags.length) roomBits.push(`${key}: ${tags.join(", ")}`);
    });
    if (roomBits.length || room.notes) {
      lines.push(`${roomName}: ${roomBits.join(" | ")}${room.notes ? ` | notes: ${room.notes}` : ""}`.trim());
    }
  }
  for (const windowEntry of data.windows) {
    const tags = [windowEntry.material, ...(windowEntry.pane || []), ...(windowEntry.action || []), windowEntry.size].filter(Boolean);
    if (windowEntry.roomName || tags.length) lines.push(`Window${windowEntry.roomName ? ` - ${windowEntry.roomName}` : ""}: ${tags.join(", ")}`);
  }
  for (const door of data.doors) {
    const tags = [door.material, ...(door.action || []), door.size].filter(Boolean);
    if (door.roomName || tags.length) lines.push(`Door${door.roomName ? ` - ${door.roomName}` : ""}: ${tags.join(", ")}`);
  }
  for (const cabinet of data.cabinetry) {
    const tags = [cabinet.material, cabinet.custom, ...(cabinet.action || [])].filter(Boolean);
    if (cabinet.roomName || tags.length) lines.push(`Cabinetry${cabinet.roomName ? ` - ${cabinet.roomName}` : ""}: ${tags.join(", ")}`);
  }
  Object.entries(data.appliances).forEach(([key, item]) => {
    if (!item.selected && !(item.values || []).length && !item.notes) return;
    lines.push(`${key.replace(/([A-Z])/g, " $1")}: ${[...(item.values || []), item.notes].filter(Boolean).join(", ")}`);
  });
  ["roof", "elevations"].forEach((key) => {
    const item = data.exterior[key];
    const materialTags = [item.material, ...(Array.isArray(item.materials) ? item.materials : [])]
      .filter(Boolean)
      .filter((value, index, arr) => arr.indexOf(value) === index);
    const tags = [...materialTags, item.size, ...(item.action || []), item.notes].filter(Boolean);
    if (tags.length) lines.push(`${key}: ${tags.join(", ")}`);
  });
  return lines.join("\n");
}

export default function InspectionChecklist() {
  const [data, setData] = React.useState(() => readStoredState());
  const [copied, setCopied] = React.useState(false);
  const [activeSurfaceByRoom, setActiveSurfaceByRoom] = React.useState({});

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const updateProperty = React.useCallback((key, value) => {
    setData((current) => ({ ...current, property: { ...current.property, [key]: value } }));
  }, []);

  const updateInteriorRoom = React.useCallback((id, key, value) => {
    setData((current) => ({
      ...current,
      interiorRooms: current.interiorRooms.map((room) => (room.id === id ? { ...room, [key]: value } : room)),
    }));
  }, []);

  const updateSurface = React.useCallback((roomId, surfaceKey, field, value) => {
    setData((current) => ({
      ...current,
      interiorRooms: current.interiorRooms.map((room) => (
        room.id === roomId ? { ...room, [surfaceKey]: { ...room[surfaceKey], [field]: value } } : room
      )),
    }));
  }, []);

  const toggleSurfaceFlag = React.useCallback((roomId, surfaceKey, flag) => {
    setData((current) => ({
      ...current,
      interiorRooms: current.interiorRooms.map((room) => {
        if (room.id !== roomId) return room;
        const currentFlags = room[surfaceKey].flags || [];
        const nextFlags = currentFlags.includes(flag)
          ? currentFlags.filter((item) => item !== flag)
          : [...currentFlags, flag];
        return { ...room, [surfaceKey]: { ...room[surfaceKey], flags: nextFlags } };
      }),
    }));
  }, []);

  const updateCollectionEntry = React.useCallback((collectionKey, id, field, value) => {
    setData((current) => ({
      ...current,
      [collectionKey]: current[collectionKey].map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    }));
  }, []);

  const toggleCollectionValue = React.useCallback((collectionKey, id, field, value) => {
    setData((current) => ({
      ...current,
      [collectionKey]: current[collectionKey].map((item) => {
        if (item.id !== id) return item;
        const currentValues = item[field] || [];
        const nextValues = currentValues.includes(value)
          ? currentValues.filter((entry) => entry !== value)
          : [...currentValues, value];
        return { ...item, [field]: nextValues };
      }),
    }));
  }, []);

  const updateAppliance = React.useCallback((key, field, value) => {
    setData((current) => ({
      ...current,
      appliances: {
        ...current.appliances,
        [key]: { ...current.appliances[key], [field]: value },
      },
    }));
  }, []);

  const toggleApplianceValue = React.useCallback((key, value) => {
    setData((current) => {
      const currentValues = current.appliances[key].values || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((entry) => entry !== value)
        : [...currentValues, value];
      return {
        ...current,
        appliances: {
          ...current.appliances,
          [key]: { ...current.appliances[key], selected: true, values: nextValues },
        },
      };
    });
  }, []);

  const updateExterior = React.useCallback((key, field, value) => {
    setData((current) => ({
      ...current,
      exterior: {
        ...current.exterior,
        [key]: { ...current.exterior[key], [field]: value },
      },
    }));
  }, []);

  const toggleExteriorAction = React.useCallback((key, value) => {
    setData((current) => {
      const currentValues = current.exterior[key].action || [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((entry) => entry !== value)
        : [...currentValues, value];
      return {
        ...current,
        exterior: {
          ...current.exterior,
          [key]: { ...current.exterior[key], action: nextValues },
        },
      };
    });
  }, []);

  const addSurfaceMaterial = React.useCallback((roomId, surfaceKey, value) => {
    const nextValue = String(value || "").trim();
    if (!nextValue) return;
    setData((current) => ({
      ...current,
      interiorRooms: current.interiorRooms.map((room) => {
        if (room.id !== roomId) return room;
        const currentValues = Array.isArray(room[surfaceKey].materials) ? room[surfaceKey].materials : [];
        if (currentValues.includes(nextValue)) return room;
        return {
          ...room,
          [surfaceKey]: { ...room[surfaceKey], materials: [...currentValues, nextValue] },
        };
      }),
    }));
  }, []);

  const removeSurfaceMaterial = React.useCallback((roomId, surfaceKey, value) => {
    setData((current) => ({
      ...current,
      interiorRooms: current.interiorRooms.map((room) => (
        room.id === roomId
          ? {
              ...room,
              [surfaceKey]: {
                ...room[surfaceKey],
                materials: (room[surfaceKey].materials || []).filter((entry) => entry !== value),
              },
            }
          : room
      )),
    }));
  }, []);

  const addExteriorMaterial = React.useCallback((key) => {
    setData((current) => {
      const nextValue = String(current.exterior[key].material || "").trim();
      if (!nextValue) return current;
      const currentValues = Array.isArray(current.exterior[key].materials) ? current.exterior[key].materials : [];
      if (currentValues.includes(nextValue)) return current;
      return {
        ...current,
        exterior: {
          ...current.exterior,
          [key]: { ...current.exterior[key], materials: [...currentValues, nextValue] },
        },
      };
    });
  }, []);

  const removeExteriorMaterial = React.useCallback((key, value) => {
    setData((current) => ({
      ...current,
      exterior: {
        ...current.exterior,
        [key]: {
          ...current.exterior[key],
          materials: (current.exterior[key].materials || []).filter((entry) => entry !== value),
        },
      },
    }));
  }, []);

  const checklistSummary = React.useMemo(() => renderSummary(data), [data]);
  const completedCount = React.useMemo(() => countCompletedValues(data), [data]);
  const totalRoomCount = data.interiorRooms.length;
  const selectedApplianceCount = Object.values(data.appliances).filter((item) => item.selected || item.values.length).length;

  const addInteriorRoom = () => setData((current) => ({ ...current, interiorRooms: [...current.interiorRooms, createInteriorRoom()] }));
  const addWindow = () => setData((current) => ({ ...current, windows: [...current.windows, createWindowEntry()] }));
  const addDoor = () => setData((current) => ({ ...current, doors: [...current.doors, createDoorEntry()] }));
  const addCabinet = () => setData((current) => ({ ...current, cabinetry: [...current.cabinetry, createCabinetEntry()] }));

  const removeCollectionItem = (collectionKey, id) => {
    setData((current) => ({
      ...current,
      [collectionKey]: current[collectionKey].filter((item) => item.id !== id),
    }));
  };

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(checklistSummary);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      setCopied(false);
    }
  };

  const handleReset = () => {
    const next = createInitialState();
    setData(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setActiveSurfaceByRoom({});
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 text-stone-900">
      <section className="overflow-hidden rounded-[28px] border border-stone-300 bg-[radial-gradient(circle_at_top_left,_rgba(180,83,9,0.16),_transparent_36%),linear-gradient(140deg,#2a211d_0%,#6b2f1f_46%,#f4e8da_100%)] shadow-[0_24px_60px_-28px_rgba(41,20,10,0.55)]">
        <div className="grid gap-6 px-6 py-6 lg:grid-cols-[1.2fr_0.8fr] lg:px-8 lg:py-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-200">Inspection Checklist</p>
            <h1 className="mt-3 max-w-3xl font-serif text-4xl text-white sm:text-5xl">Field capture built for scoping, not paper shuffling.</h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-stone-200">
              The original PM checklist is now reorganized into room-by-room surfaces, openings, millwork, appliances, and exterior scope notes. Make selections fast, keep the detail tight, and generate a usable scope summary as you go.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <SectionAnchor label="Property" href="#checklist-property" />
              <SectionAnchor label="Interior" href="#checklist-interior" />
              <SectionAnchor label="Openings" href="#checklist-openings" />
              <SectionAnchor label="Cabinetry" href="#checklist-cabinetry" />
              <SectionAnchor label="Appliances" href="#checklist-appliances" />
              <SectionAnchor label="Exterior" href="#checklist-exterior" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {[
              { label: "Captured values", value: `${completedCount}` },
              { label: "Interior room sets", value: `${totalRoomCount}` },
              { label: "Appliance groups", value: `${selectedApplianceCount}` },
            ].map((stat) => (
              <div key={stat.label} className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-[0.18em] text-stone-200">{stat.label}</p>
                <p className="mt-2 text-3xl font-semibold text-white">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <Card id="checklist-property" className="overflow-hidden border-stone-300/90">
            <CardHeader className="bg-stone-50/90">
              <h2 className="text-xl font-semibold text-stone-900">Property snapshot</h2>
              <p className="mt-1 text-sm text-stone-600">Front-load the who, where, and loss context so the rest of the checklist stays organized.</p>
            </CardHeader>
            <CardBody className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {[
                ["claimName", "Claim / job name", "Maple Ave water loss"],
                ["insured", "Insured / contact", "Jane Smith"],
                ["address", "Property address", "1234 Maple Ave"],
                ["inspector", "Inspector", "Evan"],
                ["inspectionDate", "Inspection date", "2026-05-06"],
                ["lossType", "Loss type", "Water / mold / fire / storm"],
              ].map(([key, label, placeholder]) => (
                <div key={key} className="space-y-2">
                  <FieldLabel>{label}</FieldLabel>
                  <Input value={data.property[key]} onChange={(e) => updateProperty(key, e.target.value)} placeholder={placeholder} />
                </div>
              ))}
              <div className="space-y-2 md:col-span-2 xl:col-span-3">
                <FieldLabel>General summary</FieldLabel>
                <textarea
                  value={data.property.summary}
                  onChange={(e) => updateProperty("summary", e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                  placeholder="High-level loss story, access notes, drying status, or adjuster walk-through context."
                />
              </div>
            </CardBody>
          </Card>

          <Card id="checklist-interior" className="overflow-hidden border-stone-300/90">
            <CardHeader className="flex flex-col gap-3 bg-stone-50/90 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-stone-900">Interior surfaces</h2>
                <p className="mt-1 text-sm text-stone-600">Ceiling, wall, and floor capture grouped by room instead of stacked paper boxes.</p>
              </div>
              <Button onClick={addInteriorRoom}>Add room set</Button>
            </CardHeader>
            <CardBody className="space-y-5">
              {data.interiorRooms.map((room, index) => (
                <div key={room.id} className="rounded-[24px] border border-stone-200 bg-white shadow-[0_14px_34px_-28px_rgba(120,53,15,0.45)]">
                  <div className="flex flex-col gap-3 border-b border-stone-200 bg-[linear-gradient(180deg,#fffdf8_0%,#f8f3ea_100%)] px-5 py-4 md:flex-row md:items-end md:justify-between">
                    <div className="grid flex-1 gap-3 md:grid-cols-[1.1fr_0.8fr]">
                      <div className="space-y-2">
                        <FieldLabel>Room</FieldLabel>
                        <Input value={room.roomName} onChange={(e) => updateInteriorRoom(room.id, "roomName", e.target.value)} placeholder={`Room ${index + 1}`} />
                      </div>
                      <div className="space-y-2">
                        <FieldLabel>Approximate area</FieldLabel>
                        <Input value={room.area} onChange={(e) => updateInteriorRoom(room.id, "area", e.target.value)} placeholder="e.g. 120 SF" />
                      </div>
                    </div>
                    <Button variant="ghost" className="text-red-700" onClick={() => removeCollectionItem("interiorRooms", room.id)} disabled={data.interiorRooms.length === 1}>
                      Remove
                    </Button>
                  </div>
                  <div className="space-y-4 p-5">
                    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-2">
                      <div className="grid gap-2 sm:grid-cols-3">
                        {[
                          ["ceiling", "Ceiling"],
                          ["walls", "Walls"],
                          ["floor", "Floor"],
                        ].map(([key, label]) => {
                          const isActive = (activeSurfaceByRoom[room.id] || "ceiling") === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setActiveSurfaceByRoom((current) => ({ ...current, [room.id]: key }))}
                              className={classNames(
                                "rounded-2xl px-4 py-3 text-left transition",
                                isActive ? "bg-red-700 text-white shadow-sm" : "bg-white text-stone-700 hover:bg-stone-100",
                              )}
                            >
                              <p className="text-sm font-semibold">{label}</p>
                              <p className={classNames("mt-1 text-xs", isActive ? "text-red-100" : "text-stone-500")}>
                                {(room[key].material || (room[key].materials || []).length || room[key].width || room[key].lineItem) ? "Captured" : "Not started"}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {(() => {
                      const activeKey = activeSurfaceByRoom[room.id] || "ceiling";
                      const titleMap = { ceiling: "Ceiling", walls: "Walls", floor: "Floor" };
                      return (
                        <SurfaceCard
                          title={titleMap[activeKey]}
                          kind={activeKey}
                          surface={room[activeKey]}
                          onChange={(field, value) => updateSurface(room.id, activeKey, field, value)}
                          onToggleFlag={(flag) => toggleSurfaceFlag(room.id, activeKey, flag)}
                          onAddMaterial={(value) => addSurfaceMaterial(room.id, activeKey, value)}
                          onRemoveMaterial={(value) => removeSurfaceMaterial(room.id, activeKey, value)}
                        />
                      );
                    })()}
                    <div className="space-y-2">
                      <FieldLabel>Room-level notes</FieldLabel>
                      <textarea
                        value={room.notes}
                        onChange={(e) => updateInteriorRoom(room.id, "notes", e.target.value)}
                        rows={3}
                        className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                        placeholder="Capture sequencing, containment, detached fixtures, or paint/texture continuity notes."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card id="checklist-openings" className="overflow-hidden border-stone-300/90">
            <CardHeader className="bg-stone-50/90">
              <h2 className="text-xl font-semibold text-stone-900">Openings</h2>
              <p className="mt-1 text-sm text-stone-600">Windows and doors get their own repeatable entries so selections stay flexible across rooms.</p>
            </CardHeader>
            <CardBody className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-stone-900">Windows</h3>
                    <p className="text-sm text-stone-600">Pane type, unit notes, and scope action in one row.</p>
                  </div>
                  <Button variant="secondary" onClick={addWindow}>Add window</Button>
                </div>
                <div className="grid gap-4">
                  {data.windows.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-stone-200 bg-stone-50/70 p-4">
                      <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr_auto]">
                        <div className="space-y-2">
                          <FieldLabel>Room</FieldLabel>
                          <Input value={item.roomName} onChange={(e) => updateCollectionEntry("windows", item.id, "roomName", e.target.value)} placeholder="Primary bedroom" />
                        </div>
                        <div className="space-y-2">
                          <FieldLabel>Size</FieldLabel>
                          <Input value={item.size} onChange={(e) => updateCollectionEntry("windows", item.id, "size", e.target.value)} placeholder='e.g. 36" x 48"' />
                        </div>
                        <div className="space-y-2">
                          <FieldLabel>Material / frame</FieldLabel>
                          <Input value={item.material} onChange={(e) => updateCollectionEntry("windows", item.id, "material", e.target.value)} placeholder="vinyl, clad wood, aluminum" />
                        </div>
                        <div className="flex items-end">
                          <Button variant="ghost" className="text-red-700" onClick={() => removeCollectionItem("windows", item.id)} disabled={data.windows.length === 1}>Remove</Button>
                        </div>
                      </div>
                      <div className="mt-4 grid gap-4 lg:grid-cols-2">
                        <div className="space-y-2">
                          <FieldLabel>Pane / type</FieldLabel>
                          <div className="flex flex-wrap gap-2">
                            {WINDOW_TYPES.map((option) => (
                              <ChipToggle key={option} label={option} selected={item.pane.includes(option)} onClick={() => toggleCollectionValue("windows", item.id, "pane", option)} />
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <FieldLabel>Scope action</FieldLabel>
                          <div className="flex flex-wrap gap-2">
                            {WINDOW_ACTIONS.map((option) => (
                              <ChipToggle key={option} label={option} selected={item.action.includes(option)} onClick={() => toggleCollectionValue("windows", item.id, "action", option)} />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        <FieldLabel>Notes</FieldLabel>
                        <textarea
                          value={item.notes}
                          onChange={(e) => updateCollectionEntry("windows", item.id, "notes", e.target.value)}
                          rows={2}
                          className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                          placeholder="Tempered glass, grille profile, damaged stool/apron, or moisture findings."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-stone-900">Doors</h3>
                    <p className="text-sm text-stone-600">Capture slab type, dimensions, and whether the door is reset or replaced.</p>
                  </div>
                  <Button variant="secondary" onClick={addDoor}>Add door</Button>
                </div>
                <div className="grid gap-4">
                  {data.doors.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-stone-200 bg-stone-50/70 p-4">
                      <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr_auto]">
                        <div className="space-y-2">
                          <FieldLabel>Room</FieldLabel>
                          <Input value={item.roomName} onChange={(e) => updateCollectionEntry("doors", item.id, "roomName", e.target.value)} placeholder="Hall bath" />
                        </div>
                        <div className="space-y-2">
                          <FieldLabel>Material / type</FieldLabel>
                          <SelectField value={item.material} onChange={(e) => updateCollectionEntry("doors", item.id, "material", e.target.value)} options={DOOR_MATERIALS} placeholder="Select door type" />
                        </div>
                        <div className="space-y-2">
                          <FieldLabel>Size</FieldLabel>
                          <Input value={item.size} onChange={(e) => updateCollectionEntry("doors", item.id, "size", e.target.value)} placeholder='e.g. 2/8 x 6/8' />
                        </div>
                        <div className="flex items-end">
                          <Button variant="ghost" className="text-red-700" onClick={() => removeCollectionItem("doors", item.id)} disabled={data.doors.length === 1}>Remove</Button>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {DOOR_ACTIONS.map((option) => (
                          <ChipToggle key={option} label={option} selected={item.action.includes(option)} onClick={() => toggleCollectionValue("doors", item.id, "action", option)} />
                        ))}
                      </div>
                      <div className="mt-4 space-y-2">
                        <FieldLabel>Notes</FieldLabel>
                        <textarea
                          value={item.notes}
                          onChange={(e) => updateCollectionEntry("doors", item.id, "notes", e.target.value)}
                          rows={2}
                          className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                          placeholder="Jamb damage, casing profile, threshold, weatherstrip, or hardware notes."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>

          <Card id="checklist-cabinetry" className="overflow-hidden border-stone-300/90">
            <CardHeader className="flex flex-col gap-3 bg-stone-50/90 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-stone-900">Cabinetry</h2>
                <p className="mt-1 text-sm text-stone-600">A cleaner take on the original cabinetry page with explicit action selections and room-level context.</p>
              </div>
              <Button variant="secondary" onClick={addCabinet}>Add cabinetry set</Button>
            </CardHeader>
            <CardBody className="grid gap-4">
              {data.cabinetry.map((item) => (
                <div key={item.id} className="rounded-2xl border border-stone-200 bg-stone-50/70 p-4">
                  <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr_auto]">
                    <div className="space-y-2">
                      <FieldLabel>Room</FieldLabel>
                      <Input value={item.roomName} onChange={(e) => updateCollectionEntry("cabinetry", item.id, "roomName", e.target.value)} placeholder="Kitchen" />
                    </div>
                    <div className="space-y-2">
                      <FieldLabel>Material / finish</FieldLabel>
                      <SelectField value={item.material} onChange={(e) => updateCollectionEntry("cabinetry", item.id, "material", e.target.value)} options={CABINET_MATERIALS} placeholder="Select cabinet finish" />
                    </div>
                    <div className="space-y-2">
                      <FieldLabel>Custom?</FieldLabel>
                      <SelectField value={item.custom} onChange={(e) => updateCollectionEntry("cabinetry", item.id, "custom", e.target.value)} options={["yes", "no"]} placeholder="Select" />
                    </div>
                    <div className="flex items-end">
                      <Button variant="ghost" className="text-red-700" onClick={() => removeCollectionItem("cabinetry", item.id)} disabled={data.cabinetry.length === 1}>Remove</Button>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {CABINET_ACTIONS.map((option) => (
                      <ChipToggle key={option} label={option} selected={item.action.includes(option)} onClick={() => toggleCollectionValue("cabinetry", item.id, "action", option)} />
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    <FieldLabel>Notes</FieldLabel>
                    <textarea
                      value={item.notes}
                      onChange={(e) => updateCollectionEntry("cabinetry", item.id, "notes", e.target.value)}
                      rows={2}
                      className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                      placeholder="Box construction, matching concerns, count of lowers/uppers, toe kick, or countertop coordination."
                    />
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card id="checklist-appliances" className="overflow-hidden border-stone-300/90">
            <CardHeader className="bg-stone-50/90">
              <h2 className="text-xl font-semibold text-stone-900">Appliances</h2>
              <p className="mt-1 text-sm text-stone-600">Each appliance group behaves like a quick scope selector instead of a handwritten checkbox stack.</p>
            </CardHeader>
            <CardBody className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Object.entries(data.appliances).map(([key, item]) => (
                <div key={key} className={classNames("rounded-2xl border p-4 transition", item.selected || item.values.length ? "border-red-300 bg-red-50/60" : "border-stone-200 bg-stone-50/70")}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-stone-900">{key.replace(/([A-Z])/g, " $1").replace(/^./, (match) => match.toUpperCase())}</h3>
                    <input type="checkbox" checked={item.selected} onChange={(e) => updateAppliance(key, "selected", e.target.checked)} className="h-4 w-4 rounded border-stone-300 text-red-700 focus:ring-red-300" />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {item.options.map((option) => (
                      <ChipToggle key={option} label={option} selected={item.values.includes(option)} onClick={() => toggleApplianceValue(key, option)} />
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    <FieldLabel>Notes</FieldLabel>
                    <textarea
                      value={item.notes}
                      onChange={(e) => updateAppliance(key, "notes", e.target.value)}
                      rows={2}
                      className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                      placeholder="Model/finish, utility disconnects, or reset coordination."
                    />
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card id="checklist-exterior" className="overflow-hidden border-stone-300/90">
            <CardHeader className="bg-stone-50/90">
              <h2 className="text-xl font-semibold text-stone-900">Exterior envelope</h2>
              <p className="mt-1 text-sm text-stone-600">Roof and elevation notes in a format that supports measurements, material choices, and action tags.</p>
            </CardHeader>
            <CardBody className="grid gap-4 xl:grid-cols-2">
              {[
                ["roof", "Roof"],
                ["elevations", "Elevations"],
              ].map(([key, title]) => (
                <div key={key} className="rounded-2xl border border-stone-200 bg-stone-50/70 p-4">
                  <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <FieldLabel>Material</FieldLabel>
                      <div className="flex gap-2">
                        <div className="min-w-0 flex-1">
                          <SelectField value={data.exterior[key].material} onChange={(e) => updateExterior(key, "material", e.target.value)} options={EXTERIOR_MATERIALS} placeholder="Select exterior material" />
                        </div>
                        <Button
                          variant="secondary"
                          className="shrink-0"
                          onClick={() => addExteriorMaterial(key)}
                          disabled={!String(data.exterior[key].material || "").trim()}
                        >
                          Add material
                        </Button>
                      </div>
                      <AddedMaterialList items={Array.isArray(data.exterior[key].materials) ? data.exterior[key].materials : []} onRemove={(value) => removeExteriorMaterial(key, value)} />
                    </div>
                    <div className="space-y-2">
                      <FieldLabel>Size / quantity</FieldLabel>
                      <Input value={data.exterior[key].size} onChange={(e) => updateExterior(key, "size", e.target.value)} placeholder="e.g. 18 SQ / 240 LF / front elevation" />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {["replace", "repair", "detach/reset", "paint", "seal", "match existing"].map((option) => (
                      <ChipToggle key={option} label={option} selected={data.exterior[key].action.includes(option)} onClick={() => toggleExteriorAction(key, option)} />
                    ))}
                  </div>
                  <div className="mt-4 space-y-2">
                    <FieldLabel>Notes</FieldLabel>
                    <textarea
                      value={data.exterior[key].notes}
                      onChange={(e) => updateExterior(key, "notes", e.target.value)}
                      rows={3}
                      className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100"
                      placeholder="Slope/valley detail, flashing notes, siding exposure, trim profile, or matching concerns."
                    />
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        </div>

        <aside className="space-y-5 xl:sticky xl:top-6 xl:self-start">
          <Card className="overflow-hidden border-stone-300/90">
            <CardHeader className="bg-stone-900 text-white">
              <h2 className="text-lg font-semibold">Checklist actions</h2>
            </CardHeader>
            <CardBody className="space-y-3">
              <Button className="w-full" onClick={handleCopySummary}>{copied ? "Copied summary" : "Copy scope summary"}</Button>
              <Button variant="secondary" className="w-full" onClick={handleReset}>Reset checklist</Button>
              <p className="text-xs leading-5 text-stone-600">
                Draft state is saved in this browser automatically while you work.
              </p>
            </CardBody>
          </Card>

          <Card className="overflow-hidden border-stone-300/90">
            <CardHeader className="bg-stone-50/90">
              <h2 className="text-lg font-semibold text-stone-900">Live summary</h2>
            </CardHeader>
            <CardBody>
              <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap rounded-2xl bg-stone-950 p-4 text-xs leading-6 text-amber-50">
                {checklistSummary || "Start filling out the checklist to generate a field summary here."}
              </pre>
            </CardBody>
          </Card>
        </aside>
      </div>
    </div>
  );
}
